import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import AdminLayout from '../../components/admin/AdminLayout';
import {
  discoveryApi,
  type CandidateStatus,
  type DiscoveryRun,
  type PickCandidate,
} from '../../services/discoveryApi';

const TABS: CandidateStatus[] = ['PENDING', 'APPROVED', 'REJECTED'];
const TAB_LABEL: Record<CandidateStatus, string> = {
  PENDING: 'To review',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

const usd = (micro: number | null) => (micro === null ? '' : `$${(micro / 1e6).toFixed(2)}`);

/**
 * /admin/picks/candidates (PRD §6.4): what the weekly discovery run found. Review
 * opens the Pick form prefilled; approving publishes it. The review summary and its
 * sources are for the team only and never copied to a public field.
 */
export default function AdminPickCandidates() {
  const [tab, setTab] = useState<CandidateStatus>('PENDING');
  const [items, setItems] = useState<PickCandidate[]>([]);
  const [runs, setRuns] = useState<DiscoveryRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, r] = await Promise.all([discoveryApi.candidates(tab), discoveryApi.runs()]);
      setItems(c);
      setRuns(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    void load();
  }, [load]);

  const reject = async (c: PickCandidate) => {
    const reason = window.prompt('Why is this not a fit? (kept for the record)');
    if (!reason?.trim()) return;
    setBusy(c.id);
    try {
      await discoveryApi.reject(c.id, reason.trim());
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reject failed');
    } finally {
      setBusy(null);
    }
  };

  const runNow = async () => {
    setBusy('run');
    setError(null);
    try {
      await discoveryApi.runNow();
      setNotice('Started. It takes up to 15 minutes; refresh to see new candidates.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start a run');
    } finally {
      setBusy(null);
    }
  };

  return (
    <AdminLayout
      title="Pick candidates"
      headerRight={
        <div className="flex gap-2">
          <Link
            to="/admin/picks/topics"
            className="inline-flex items-center rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
          >
            Topics
          </Link>
          <button
            type="button"
            disabled={busy === 'run'}
            onClick={() => void runNow()}
            className="inline-flex items-center rounded-lg bg-primary px-4 py-2 text-white transition-colors hover:bg-primary-dark disabled:opacity-50"
          >
            Run now
          </button>
        </div>
      }
    >
      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}
      {notice && (
        <div className="mb-4 rounded-lg bg-primary-pale p-3 text-sm text-gray-800">{notice}</div>
      )}
      <nav className="mb-4 flex gap-2">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`rounded-full px-4 py-1.5 text-sm ${
              tab === t ? 'bg-primary text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            {TAB_LABEL[t]}
          </button>
        ))}
      </nav>

      {loading ? (
        <p className="text-gray-600">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-gray-600">Nothing here.</p>
      ) : (
        <div className="space-y-4">
          {items.map((c) => (
            <div key={c.id} className="rounded-lg bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-medium text-gray-900">
                    {c.draft.titleCN || c.draft.titleEN || c.draft.providerName}
                  </div>
                  <div className="text-sm text-gray-600">
                    {c.draft.providerName} · {c.draft.type === 'EVENT' ? 'Event' : 'Provider'}
                    {c.confidence !== null && ` · confidence ${Math.round(c.confidence * 100)}%`}
                    {c.run && ` · run ${c.run.weekKey}`}
                  </div>
                  {c.draft.externalUrl && (
                    <a
                      href={c.draft.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline"
                    >
                      {c.draft.externalUrl}
                    </a>
                  )}
                </div>
                {c.status === 'PENDING' ? (
                  <div className="flex gap-2">
                    <Link
                      to={`/admin/picks/new?candidate=${c.id}`}
                      className="rounded-lg bg-primary px-3 py-1.5 text-sm text-white hover:bg-primary-dark"
                    >
                      Review and publish
                    </Link>
                    <button
                      type="button"
                      disabled={busy === c.id}
                      onClick={() => void reject(c)}
                      className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
                    >
                      Reject
                    </button>
                  </div>
                ) : c.status === 'APPROVED' && c.createdPickId ? (
                  <Link
                    to={`/admin/picks/${c.createdPickId}`}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    Open pick
                  </Link>
                ) : (
                  <span className="text-sm text-gray-600">{c.rejectionReason}</span>
                )}
              </div>
              {c.reviewSummary && (
                <div className="mt-3 whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
                  <div className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                    What reviews say (team only)
                  </div>
                  {c.reviewSummary}
                </div>
              )}
              {(c.whyPickedDraftCN || c.whyPickedDraftEN) && (
                <div className="mt-3 text-sm text-gray-700">
                  <span className="font-medium">Draft reason: </span>
                  {c.whyPickedDraftCN || c.whyPickedDraftEN}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <h2 className="mb-3 mt-10 text-lg font-semibold text-gray-900">Recent runs</h2>
      {runs.length === 0 ? (
        <p className="text-sm text-gray-600">No runs yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">Week</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Started</th>
                <th className="px-4 py-3">Topics</th>
                <th className="px-4 py-3">Candidates</th>
                <th className="px-4 py-3">Cost</th>
                <th className="px-4 py-3">Note</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {runs.map((r) => (
                <tr key={r.id}>
                  <td className="whitespace-nowrap px-4 py-3">{r.weekKey}</td>
                  <td className="px-4 py-3">{r.status}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-gray-600">
                    {new Date(r.startedAt).toLocaleString('en-US')}
                  </td>
                  <td className="px-4 py-3">{r.topicCount}</td>
                  <td className="px-4 py-3">{r.candidateCount}</td>
                  <td className="px-4 py-3">{usd(r.costMicroUsd)}</td>
                  <td className="max-w-[360px] px-4 py-3 text-gray-600">{r.error}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
